import SeoHead from '@atoms/SeoHead';
import Layout from '@molecules/Layout';
import type { LayoutProps } from '@molecules/Layout/Layout.types';
import { getClient } from '@sanity/lib/getClient';
import type { ISanityCategory } from '@sanity/types/category';
import ArticleListView from '@views/ArticleListView';
import type { ArticleListViewProps } from '@views/ArticleListView/ArticleListView.types';
import type { GetStaticPaths, GetStaticProps } from 'next';
import type { ReactElement } from 'react';

interface SingleTagListProps {
  layoutData: Omit<LayoutProps, 'children'>;
  tag: ISanityCategory;
  tagsListData: ArticleListViewProps;
}

const SingleTagList = ({
  tag,
  layoutData,
  tagsListData,
}: SingleTagListProps): ReactElement => (
  <>
    <SeoHead
      metaDescription={tag.description}
      noIndex
      title={`Kierunek ${tag.title}`}
    />
    <Layout {...layoutData}>
      <ArticleListView {...tagsListData} />
    </Layout>
  </>
);

export const getStaticPaths: GetStaticPaths = async () => {
  const client = getClient();

  const { getAllCategories } = await import('@lib/sanity');
  const categories = await getAllCategories(client);

  const paths = categories.map(({ slug }) => ({
    params: {
      tag: slug,
    },
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<SingleTagListProps> = async ({
  params,
}) => {
  const client = getClient();

  const { mapLayout } = await import('@mappers/layout');
  const { mapPostList } = await import('@mappers/postList');
  const { getBasicData, getAllCategories, getPostsByCategorySlug } =
    await import('@lib/sanity');

  const currentCategory = params.tag as string;
  const categories = await getAllCategories(client);
  const layoutData = mapLayout(categories);
  const currentCategoryPosts = await getPostsByCategorySlug(
    client,
    currentCategory,
  );
  const currentCategoryData = categories.find(
    ({ slug }) => slug === currentCategory,
  );
  const basicData = await getBasicData(client);

  const categoryListData = mapPostList({
    posts: currentCategoryPosts,
    tag: currentCategoryData,
    ...basicData,
  });

  return {
    props: {
      tag: currentCategoryData,
      layoutData,
      tagsListData: categoryListData,
    },
  };
};

export default SingleTagList;
